import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useNavigationPermissions } from "@/hooks/useNavigationPermissions";
import AuthGuard from "./AuthGuard";
import { useState, useEffect } from "react";

type ParentAuthWrapperProps = {
  children: React.ReactNode;
};

const ParentAuthWrapper = ({ children }: ParentAuthWrapperProps) => {
  const { user, userRole, loading } = useAuth();
  const { canAccessParentRoutes, canAccessAdminRoutes } = useNavigationPermissions();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Use a forced delay to prevent 401 flashes
  useEffect(() => {
    if (!loading) {
      console.log("ParentAuthWrapper: Setting up authorization delay", { user: !!user });
      const timer = setTimeout(() => {
        console.log("ParentAuthWrapper: Authorization delay complete");
        setIsAuthorized(true);
      }, user ? 2500 : 1000); // CRITICAL: 2500ms delay required for mobile browser race condition fix - DO NOT REDUCE
      return () => clearTimeout(timer);
    }
  }, [loading, user]);

  // Show loading spinner while waiting for the delay to complete
  if (loading || !isAuthorized) {
    console.log("ParentAuthWrapper: Showing loading spinner during authorization delay");
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="text-primary font-medium">
            {user ? "Loading Parent Portal" : "Redirecting to login..."}
          </p>
        </div>
      </div>
    );
  }

  // Check permissions after auth is established and delay is complete
  if (canAccessAdminRoutes) {
    console.log("Admin viewing parent portal, allowing access");
    return <>{children}</>;
  }

  // Check if user is authenticated and has family_id in metadata or can access parent routes
  if (!user || (!user.user_metadata?.family_id && !canAccessParentRoutes)) {
    console.log("No user with family_id found, redirecting to app domain auth with signin tab");
    // Preserve full URL including query parameters for document request links
    const fullPath = location.pathname + location.search;
    
    // Check if we're on marketing domain and need to redirect to app domain
    if (typeof window !== 'undefined' && !window.location.hostname.includes('app.')) {
      // On marketing domain - redirect to app domain for auth
      const appUrl = import.meta.env.DEV 
        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
      window.location.href = appUrl + '/auth?tab=signin';
      return null;
    } else {
      // Already on app domain, use normal navigation
      return <Navigate to="/auth?tab=signin" state={{ from: fullPath }} replace />;
    }
  }

  console.log("Parent/guardian authenticated, allowing access");
  return <>{children}</>;
};

export default ParentAuthWrapper;
